// Tableau de bord (R-TDB-01 à R-TDB-07).
import { useState } from 'react';
import { Link } from 'react-router';
import { useMagasin, magasin } from '../donnees/magasin';
import { jourPedagogique, formaterDate, ajouterJours, jourIso } from '@partage/dates';
import { NOMS_DOMAINES } from '@partage/contenu';
import {
  sessionSuivante, sessionEnCours, semaineDeProgramme, progressionParDomaine, grilleRegularite, serieRegularite,
  prochainCreneau, fileDeRevision, defiDeLaSemaine, prochaineSoutenance, sessionsDe,
} from '@partage/programme';
import { GrilleRegularite } from '../composants/GrilleRegularite';

function lundiProchain(aujourdhui: string): string {
  const decalage = (8 - jourIso(aujourdhui)) % 7 || 7;
  return ajouterJours(aujourdhui, decalage);
}

export function TableauDeBord() {
  const { etat, bundles, parcours, connecte, catalogue } = useMagasin();
  const bundle = bundles[etat.saison_courante];
  const aujourdhui = jourPedagogique(new Date());
  const [debut, setDebut] = useState(lundiProchain(aujourdhui));

  if (!bundle) {
    return (
      <div className="carte">
        <h2>Contenu de la saison {etat.saison_courante} indisponible</h2>
        {connecte ? (
          catalogue && !catalogue.saisons.some((s) => s.numero === etat.saison_courante)
            ? <p>Le contenu de cette saison n’est pas encore publié sur le serveur.</p>
            : <p>Téléchargement en cours… <button onClick={() => void magasin.chargerContenu()}>Réessayer</button></p>
        ) : <p>Connecte-toi une fois avec du réseau pour télécharger la saison (elle restera disponible hors ligne).</p>}
      </div>
    );
  }

  const activee = Boolean(etat.parametres.debut_saisons[String(bundle.numero)]);
  if (!activee) {
    return (
      <div className="carte session-suivante">
        <h2>Saison {bundle.numero} — {bundle.titre}</h2>
        <p className="objectif">{bundle.objectif}</p>
        <p><strong>Livrable :</strong> {bundle.livrable.titre}. {bundle.livrable.description}</p>
        <p><strong>Ce que l’IA a le droit de faire :</strong> {bundle.regle_ia}</p>
        <div className="champ" style={{ maxWidth: 280 }}>
          <label htmlFor="debut">Date de début de la saison (un lundi)</label>
          <input id="debut" type="date" value={debut} onChange={(e) => setDebut(e.target.value)} />
          <div className="aide-champ">Sert au planning et à la grille de régularité, jamais à calculer un retard.</div>
        </div>
        <button className="principal" onClick={() => void magasin.emettre('saison_activee', { saison: bundle.numero, debut, mode_agile: bundle.mode_agile, livrable: bundle.livrable })}>
          Activer la saison {bundle.numero}
        </button>
      </div>
    );
  }

  const suivante = sessionSuivante(etat, bundle);
  const enCours = sessionEnCours(etat, bundle);
  const cible = enCours ?? suivante;
  const semaine = semaineDeProgramme(etat, bundle);
  const grille = grilleRegularite(etat, bundle.numero, aujourdhui);
  const serie = serieRegularite(grille, aujourdhui);
  const dues = fileDeRevision(etat, aujourdhui).length;
  const defi = defiDeLaSemaine(etat, bundle);
  const soutenance = prochaineSoutenance(etat, bundle);
  // R-PAR-04 : les parcours annexes activés comptent dans la progression par domaine.
  const domaines = progressionParDomaine(etat, bundle, Object.values(parcours));
  const derniereEntree = [...etat.journal].reverse().find((e) => !e.archivee && e.type !== 'note_mentor');
  const derniereNote = [...etat.journal].reverse().find((e) => !e.archivee && e.type === 'note_mentor');
  // R-PAR-03 : ne compter QUE les sessions de la saison — une session de parcours annexe
  // ne doit pas faire avancer le « n / 33 ».
  const terminees = sessionsDe(bundle).filter((s) => etat.progression.sessions[s.id]?.statut === 'terminee').length;
  // Le total vient du contenu réellement chargé, pas d'un 33 écrit en dur : tant que la saison
  // n'est pas complète, annoncer 33 laisserait croire que les sessions manquantes existent.
  const total = sessionsDe(bundle).length;

  return (
    <>
      <div className="carte session-suivante">
        <div className="ligne">
          <span className="badge accent">Semaine de programme {semaine} / 11</span>
          <span className="badge">Niveau IA {bundle.niveau_ia}</span>
          <span className="badge">{terminees} / {total} session{total > 1 ? 's' : ''} écrite{total > 1 ? 's' : ''}</span>
        </div>
        {cible ? (
          <>
            <h2 style={{ marginTop: '0.6em' }}>{enCours ? 'Session en cours' : 'Session suivante'} · {cible.id} — {cible.titre}</h2>
            <p className="objectif"><strong>Ce soir tu vas faire marcher :</strong> {cible.resultat_visible}</p>
            <div className="ligne">
              <Link className="bouton principal" to={`/sessions/${cible.id}`}>{enCours ? '▶ Reprendre' : 'Commencer'}</Link>
              <span className="doux petit-texte">Prochain créneau : {formaterDate(prochainCreneau(etat, aujourdhui))}</span>
            </div>
          </>
        ) : (
          <>
            <h2>Toutes les sessions de la saison sont terminées 🎉</h2>
            <p>Reste la soutenance de fin de saison (Défis et soutenances), puis « Passer à la saison suivante » dans Paramètres.</p>
          </>
        )}
      </div>

      <div className="tuiles">
        <Link to="/reviser" className="tuile" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="titre">Cartes à réviser</div>
          <div className="valeur">{dues}</div>
        </Link>
        <div className="tuile">
          <div className="titre">Régularité</div>
          <div className="valeur">{serie} sem.</div>
          <div className="petit-texte doux">d’affilée</div>
        </div>
        <Link to="/defis" className="tuile" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="titre">Défi de la semaine</div>
          <div style={{ fontWeight: 600 }}>{defi?.titre ?? '—'}</div>
          <div className="petit-texte doux">{defi?.etat === 'fait' ? 'Fait ✓' : defi?.etat === 'passe' ? 'Passé, sans pénalité' : 'Proposé pour vendredi'}</div>
        </Link>
        <div className="tuile">
          <div className="titre">Prochaine soutenance</div>
          <div style={{ fontWeight: 600 }}>{soutenance ? `Semaine ${soutenance.semaine}` : '—'}</div>
          <div className="petit-texte doux">{soutenance?.titre}</div>
        </div>
      </div>

      <div className="grille">
        <div className="carte">
          <h3>Régularité de la saison (par semaine)</h3>
          <GrilleRegularite grille={grille} />
          <p className="petit-texte doux" style={{ marginTop: '0.6em' }}>Complète = 3 sessions ; partielle = 1 ou 2 ; hachurée = off. Le vendredi n’est jamais compté comme manqué.</p>
        </div>
        <div className="carte">
          <h3>Progression par domaine</h3>
          <div className="domaines">
            {Object.entries(domaines).map(([d, { faites, total }]) => (
              <div className="domaine" key={d}>
                <span>{NOMS_DOMAINES[d as keyof typeof NOMS_DOMAINES] ?? d}</span>
                <div className="progression"><div style={{ width: `${total ? (100 * faites) / total : 0}%` }} /></div>
                <span className="petit-texte doux">{faites}/{total}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grille" style={{ marginTop: '1em' }}>
        <div className="carte">
          <h3>Dernière entrée du journal</h3>
          {derniereEntree ? <p style={{ whiteSpace: 'pre-wrap' }}><span className="doux petit-texte">{new Date(derniereEntree.le).toLocaleDateString('fr-FR')} · {derniereEntree.session ?? derniereEntree.type}</span><br />{derniereEntree.texte}</p> : <p className="vide">Rien encore : ta première clôture de session l’inaugurera.</p>}
        </div>
        <div className="carte">
          <h3>Dernière note du mentor</h3>
          {derniereNote ? <p style={{ whiteSpace: 'pre-wrap' }}><span className="doux petit-texte">{new Date(derniereNote.le).toLocaleDateString('fr-FR')} · {derniereNote.sous_type}</span><br />{derniereNote.texte}</p> : <p className="vide">Le mentor (Claude Code) écrira ici après une review, un défi ou une soutenance.</p>}
        </div>
      </div>
    </>
  );
}
